import type { EntityType } from "@config";
import type { TElement } from "@editor/modules/menu/modules/elements-library/types";
import { useNewSlidesStore } from "@editor/modules/editor/stores/use-new-slides-store";
import type { ITableCell, ITableSettings } from "@library-elements/table";
import { isAllowedInCell } from "@library-elements/table/constants/allowed-in-cell-elements";
import { extractCellText } from "@library-elements/table/utils/extract-cell-text";
import { getCellKind } from "@library-elements/table/utils/get-cell-kind";
import { nanoid } from "@reduxjs/toolkit";
import { store, useAppSelector } from "@store";
import { createEntity } from "@store/story";
import {
  getActiveEntityId,
  getEntitySettings,
  getTableActiveCellsIds,
  getTableCellChildrenIndex,
  selectActiveInCellOwner,
} from "@store/story/selectors";
import { resolveAbsoluteIndex } from "@store/story/utils/apply-in-cell-element-move";
import { editTableCell } from "../utils/edit-table-entity";
import { isTableLockedInVersion } from "../utils/is-table-locked-in-version";
import { isTextAreaElement } from "../utils/is-text-area-element";
import { escapeHtml } from "../utils/table-clipboard-parse";
import { buildCellTextTransform } from "../utils/transform-cell-text";
import { triggerContentUpdateFor } from "@/shared/utils/content-trigger-registry";

const inheritTableTextAlign = (html: string): string =>
  html.replace(/text-align:\s*[^;"']+;?/gi, "");

// The in-cell element entity a copied or picked element becomes: `animation`
// dropped (in-cell elements don't animate), text-align inherited from the
// table, and homed on (tableEntityId, cellId). Shared with the replace path in
// tryPasteShortcutElementIntoActiveCell, which hands it to the paste planner.
export const buildInCellElementEntity = (args: {
  element: TElement;
  tableEntityId: string;
  cellId: string;
  id?: string;
}): EntityType => {
  const { element, tableEntityId, cellId, id } = args;
  const { animation: _drop, ...settings } = element.json.settings as Record<
    string,
    unknown
  >;
  if (typeof settings.content === "string") {
    settings.content = inheritTableTextAlign(settings.content);
  }
  return {
    id: id ?? nanoid(),
    type: "element",
    parentId: tableEntityId,
    childIds: [],
    settings: { ...settings, cellId },
  };
};

// Imperative in-cell insert (shared by the sidebar click-picker and
// paste-into-cell): builds the new element child (plus, on the FIRST insert
// into a text-bearing cell, a RichText child adopting the cell's text) and
// dispatches ONE insert-into-cell createEntity — one history step, one
// broadcast, one Yjs batch. Callers own selection/allow-list gating.
//
// `cellIndex` is the cell-local slot among the visual bands (virtual plain
// text at 0 when present, then existing element children). Omitted → append.
export const insertElementIntoCell = (args: {
  cell: ITableCell;
  tableEntityId: string;
  element: TElement;
  cellIndex?: number;
}): void => {
  const { cell, tableEntityId, element, cellIndex } = args;
  const cellId = cell.id;

  // Calc cells are exclusive with children.
  if (getCellKind(cell) === "calc") return;

  const state = store.getState();
  // A version's locked table is edited only through its data sheet.
  if (isTableLockedInVersion(tableEntityId, state)) return;
  const hasChildren =
    (getTableCellChildrenIndex(tableEntityId)(state)[cellId]?.length ?? 0) > 0;

  const elSettings = element.json.settings as {
    name?: string;
    content?: string;
    placeholder?: string;
  };

  // TRANSFORM: a plain text element dropped on an EMPTY, child-less text cell
  // restyles the cell's native text area in place (adopt the dropped block, no
  // child) — the reliable edit path. A non-empty cell falls through to ADD.
  if (
    !hasChildren &&
    isTextAreaElement(elSettings) &&
    extractCellText(cell.content ?? "").length === 0
  ) {
    const next = buildCellTextTransform({ content: cell.content }, elSettings);
    const transformContent = inheritTableTextAlign(next.content);
    editTableCell(
      (draft) => {
        draft.content = transformContent;
      },
      { cellId, entityId: tableEntityId },
    );
    // Push the new content into the (possibly already-mounted) cell Froala so
    // it can't save its stale content back over the transform. The per-cell
    // instance uses id `${tableEntityId}-${cellId}` (see the ADD path below);
    // the table-level `-table-froala` id is triggered too, mirroring
    // tableClearActiveCells.
    triggerContentUpdateFor(`${tableEntityId}-${cellId}`, transformContent);
    triggerContentUpdateFor(`${tableEntityId}-table-froala`, transformContent);
    useNewSlidesStore.getState().requestFocus(`${tableEntityId}-table-froala`);
    return;
  }

  const entities: Record<string, EntityType> = {};
  const rootIds: string[] = [];
  let content: string | undefined;
  let priorContent: string | undefined;
  const hasPlainText = extractCellText(cell.content ?? "").length > 0;

  // First element insert converts the cell's existing text into a RichText
  // child so text and elements stack together; the cell's native text empties.
  // `cellIndex === 0` places the new element above that RichText (above the
  // former plain-text band); any other index keeps RichText first.
  let richTextId: string | undefined;
  if (!hasChildren && hasPlainText) {
    richTextId = nanoid();
    entities[richTextId] = {
      id: richTextId,
      type: "element",
      parentId: tableEntityId,
      childIds: [],
      settings: { name: "RichText", content: cell.content, cellId },
    };
    content = "";
    priorContent = cell.content;
  }

  const elementEntity = buildInCellElementEntity({
    element,
    tableEntityId,
    cellId,
  });
  const elementId = elementEntity.id;
  entities[elementId] = elementEntity;

  if (richTextId) {
    if (cellIndex === 0) {
      rootIds.push(elementId, richTextId);
    } else {
      rootIds.push(richTextId, elementId);
    }
  } else {
    rootIds.push(elementId);
  }

  // Resolve a cell-local visual index to an absolute table.childIds splice.
  // When converting text→RichText, both roots are spliced together at the
  // cell's append/absolute slot (empty siblings → end of table.childIds).
  let absoluteIndex: number | undefined;
  if (cellIndex !== undefined) {
    const table = state.story.flatContentLayers[tableEntityId];
    if (table) {
      // Among existing element siblings (pre-insert). With a virtual text
      // band, visual 0 = above text (element-first in rootIds), visual ≥1
      // maps to element slot visual-1; with no virtual text, visual = slot.
      const elementSlot =
        hasPlainText && !hasChildren
          ? 0
          : hasPlainText
            ? Math.max(0, cellIndex - 1)
            : cellIndex;
      absoluteIndex = resolveAbsoluteIndex(
        state.story.flatContentLayers,
        table.childIds,
        cellId,
        elementSlot,
      );
    }
  }

  store.dispatch(
    createEntity({
      feature: "insert-into-cell",
      entities,
      rootIds,
      tableEntityId,
      cellId,
      content,
      priorContent,
      ...(absoluteIndex !== undefined ? { index: absoluteIndex } : {}),
    }),
  );

  // The text→RichText conversion emptied cell.content in the store, but the
  // cell was active during the insert, so its already-mounted Froala still
  // holds the old text (and would show it — or save it back — while active).
  // Push the emptied content into both the per-cell trigger and the table's
  // Froala instance, mirroring tableClearActiveCells.
  if (content !== undefined) {
    triggerContentUpdateFor(`${tableEntityId}-${cellId}`, content);
    triggerContentUpdateFor(`${tableEntityId}-table-froala`, content);
  }
};

// Imperative gate for non-hook insert surfaces (media library dialog/panels,
// AI image): when the current selection targets a single table cell (or an
// in-cell child is active) and the element is cell-allowed, insert INTO the
// cell and return true; return false to let the caller's slide-level insert
// proceed.
export const tryInsertElementIntoActiveCell = (element: TElement): boolean => {
  if (!isAllowedInCell(element.json.settings.name as string)) return false;

  const state = store.getState();
  const owner = selectActiveInCellOwner(state);
  let tableEntityId = owner?.tableId;
  let cellId = owner?.cellId;
  if (!tableEntityId || !cellId) {
    const activeId = getActiveEntityId(state);
    const active = activeId
      ? state.story.flatContentLayers[activeId]
      : undefined;
    const activeCellsIds = getTableActiveCellsIds(state);
    if (
      activeId &&
      active?.settings.name === "Table" &&
      activeCellsIds.length === 1
    ) {
      tableEntityId = activeId;
      cellId = activeCellsIds[0];
    }
  }
  if (!tableEntityId || !cellId) return false;
  // A version's locked table takes nothing: let the caller insert on the
  // slide instead of reporting an insert that insertElementIntoCell refuses.
  if (isTableLockedInVersion(tableEntityId, state)) return false;

  const settings = getEntitySettings<ITableSettings>(tableEntityId)(state) as
    | ITableSettings
    | undefined;
  const cell = settings?.tableData?.find(
    (c) => c.id === cellId && !c.mergedInto,
  );
  if (!cell || getCellKind(cell) === "calc") return false;

  insertElementIntoCell({ cell, tableEntityId, element });
  return true;
};

// Paste of a loose text value while an in-cell element is the SELECTION: the
// rule adds it into the cell after that element, so it becomes a RichText
// sibling. Froala covers this only when the caret sits in an editable text
// element — with an image (or any other non-text element) selected there is no
// caret to insert into, which is why such a paste appeared to do nothing at
// all. Returns false when the selection is not an in-cell element of a live,
// non-calc cell, so the caller can fall back.
export const insertTextAfterActiveInCellElement = (text: string): boolean => {
  const state = store.getState();
  const owner = selectActiveInCellOwner(state);
  if (!owner) return false;
  const { tableId, cellId } = owner;

  const settings = getEntitySettings<ITableSettings>(tableId)(state) as
    | ITableSettings
    | undefined;
  const cell = settings?.tableData?.find(
    (c) => c.id === cellId && !c.mergedInto,
  );
  if (!cell || getCellKind(cell) === "calc") return false;

  const childRootIds = getTableCellChildrenIndex(tableId)(state)[cellId] ?? [];
  const activeId = getActiveEntityId(state);
  const position = activeId ? childRootIds.indexOf(activeId) : -1;
  if (position < 0) return false;

  // Visual bands: the cell's own plain text holds band 0 when it has any, then
  // the element children in order. The slot AFTER the active element is
  // therefore its own band + 1 — insertElementIntoCell maps that back to an
  // element slot and on to an absolute table.childIds splice.
  const hasPlainText = extractCellText(cell.content ?? "").length > 0;
  const cellIndex = (hasPlainText ? 1 : 0) + position + 1;

  // One <p> per line: a multi-line value reaches here only when it has no tabs
  // and no consistent commas, so it is prose, not a grid.
  const content = text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .filter((line) => line.length > 0)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");
  if (!content) return false;

  insertElementIntoCell({
    cell,
    tableEntityId: tableId,
    cellIndex,
    element: {
      json: { settings: { name: "RichText", content } },
      category: "",
      element: "RichText",
      order: 0,
      previewImage: null,
      id: "pasted-text",
    } as unknown as TElement,
  });
  return true;
};

// Click-to-insert gate for a single cell: allowed when the cell is the sole
// selection, or one of its in-cell children is the active element.
export const useInsertElementIntoCell = (
  cellId: string,
  tableEntityId: string,
) => {
  const activeCellsIds = useAppSelector(getTableActiveCellsIds);
  const activeOwner = useAppSelector(selectActiveInCellOwner);
  // Read TABLE settings from the passed id, not the active entity (which may
  // be an in-cell child with no tableData).
  const settings = useAppSelector((state) =>
    getEntitySettings<ITableSettings>(tableEntityId)(state),
  ) as ITableSettings | undefined;

  const cell = settings?.tableData?.find((c) => c.id === cellId);
  const kind = cell ? getCellKind(cell) : undefined;
  const isCellSelected =
    activeCellsIds.length === 1 && activeCellsIds[0] === cellId;
  const isChildOfCellActive =
    !!cellId &&
    activeOwner?.cellId === cellId &&
    activeOwner?.tableId === tableEntityId;
  const canInsert =
    !!cell && kind !== "calc" && (isCellSelected || isChildOfCellActive);

  const pickElement = (element: TElement) => {
    if (!canInsert || !cell) return;
    insertElementIntoCell({ cell, tableEntityId, element });
  };

  return { canInsert, pickElement };
};
